// components/QuickAddProductModal.jsx - Quick Add Product Modal
import React, { useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import * as api from '../services/api'

export default function QuickAddProductModal({ shortCode, onAdd, onClose }) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    epaNumber: '',
    activeIngredient: '',
    percentActiveIngredient: '',
    defaultUnit: 'oz',
    supplier: '',
    notes: ''
  })
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      setError('Product name is required')
      return
    }
    
    setSaving(true)
    setError(null)
    
    try {
      const result = await api.addProduct(shortCode, formData)
      onAdd(result.product || formData)
    } catch (err) {
      setError(err.message || 'Failed to add product')
      setSaving(false)
    }
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-xl 
                      max-h-[90vh] overflow-y-auto animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Add New Product</h3>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          
          <div>
            <label className="form-label">
              Product Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g., Transtect 70WSP"
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              className="form-input"
              autoFocus
            />
          </div>
          
          <div>
            <label className="form-label">EPA Registration Number</label>
            <input
              type="text"
              placeholder="e.g., 59639-145"
              value={formData.epaNumber}
              onChange={(e) => updateField('epaNumber', e.target.value)}
              className="form-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Active Ingredient</label>
              <input
                type="text"
                placeholder="e.g., Dinotefuran"
                value={formData.activeIngredient}
                onChange={(e) => updateField('activeIngredient', e.target.value)}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">% Active</label>
              <input
                type="text"
                placeholder="e.g., 70"
                value={formData.percentActiveIngredient}
                onChange={(e) => updateField('percentActiveIngredient', e.target.value)}
                className="form-input"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Default Unit</label>
              <select
                value={formData.defaultUnit}
                onChange={(e) => updateField('defaultUnit', e.target.value)}
                className="form-select"
              >
                <optgroup label="Volume">
                  <option value="mL">mL (milliliters)</option>
                  <option value="L">L (liters)</option>
                  <option value="fl oz">fl oz (fluid ounces)</option>
                  <option value="pt">pt (pints)</option>
                  <option value="qt">qt (quarts)</option>
                  <option value="gal">gal (gallons)</option>
                </optgroup>
                <optgroup label="Weight">
                  <option value="g">g (grams)</option>
                  <option value="kg">kg (kilograms)</option>
                  <option value="oz">oz (ounces)</option>
                  <option value="lb">lb (pounds)</option>
                </optgroup>
              </select>
            </div>
            <div>
              <label className="form-label">Supplier</label>
              <input
                type="text"
                placeholder="e.g., Target Specialty"
                value={formData.supplier}
                onChange={(e) => updateField('supplier', e.target.value)}
                className="form-input"
              />
            </div>
          </div>
          
          <div>
            <label className="form-label">Notes</label>
            <textarea
              placeholder="Optional notes about this product"
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              className="form-input"
              rows={2}
            />
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Add Product'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
